import {useEffect, useRef} from 'react'
import {createPortal} from 'react-dom'
import {FontScale, useAccessibility} from './AccessibilityProvider'

type ToggleRowProps = {
  checked: boolean
  description: string
  icon: string
  label: string
  onChange: (checked: boolean) => void
}

const ToggleRow = ({checked, description, icon, label, onChange}: ToggleRowProps) => (
  <label className='nosi-a11y-option'>
    <span className='nosi-a11y-option__icon' aria-hidden='true'>
      <i className={`bi ${icon}`}></i>
    </span>
    <span className='nosi-a11y-option__copy'>
      <strong>{label}</strong>
      <small>{description}</small>
    </span>
    <span className='form-check form-switch m-0'>
      <input
        className='form-check-input'
        type='checkbox'
        role='switch'
        checked={checked}
        aria-label={label}
        onChange={(event) => onChange(event.target.checked)}
      />
    </span>
  </label>
)

const FONT_SCALES: Array<{label: string; value: FontScale}> = [
  {label: 'Standart', value: 1},
  {label: '%110', value: 1.1},
  {label: '%120', value: 1.2},
  {label: '%130', value: 1.3},
]

const AccessibilityWidget = () => {
  const {close, isOpen, preferences, reset, setPreference, toggle} = useAccessibility()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    closeRef.current?.focus()
    document.body.classList.add('nosi-a11y-dialog-open')
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
        window.setTimeout(() => triggerRef.current?.focus(), 0)
        return
      }

      if (event.key !== 'Tab' || !panelRef.current) {
        return
      }

      const focusableElements = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter(
        (element) =>
          !(element as HTMLButtonElement).disabled && element.getAttribute('aria-hidden') !== 'true'
      )

      if (focusableElements.length === 0) {
        event.preventDefault()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const activeElement = document.activeElement

      if (!panelRef.current.contains(activeElement)) {
        event.preventDefault()
        ;(event.shiftKey ? lastElement : firstElement).focus()
      } else if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.classList.remove('nosi-a11y-dialog-open')
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [close, isOpen])

  const closeAndRestoreFocus = () => {
    close()
    window.setTimeout(() => triggerRef.current?.focus(), 0)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        className='btn btn-icon nosi-topbar-button'
        aria-label='Erişilebilirlik ayarlarını aç'
        aria-expanded={isOpen}
        title='Erişilebilirlik'
        onClick={toggle}
      >
        <i className='bi bi-universal-access-circle fs-2' aria-hidden='true'></i>
      </button>

      {isOpen &&
        createPortal(
          <div className='nosi-a11y-layer'>
            <button
              type='button'
              className='nosi-a11y-backdrop'
              tabIndex={-1}
              aria-label='Erişilebilirlik panelini kapat'
              onClick={closeAndRestoreFocus}
            />
            <section
              ref={panelRef}
              className='nosi-a11y-panel'
              role='dialog'
              aria-modal='true'
              aria-labelledby='nosi-a11y-title'
            >
              <header className='nosi-a11y-panel__header'>
                <div>
                  <span className='nosi-a11y-panel__eyebrow'>Kişisel görünüm</span>
                  <h2 id='nosi-a11y-title'>Erişilebilirlik ayarları</h2>
                </div>
                <button
                  ref={closeRef}
                  type='button'
                  className='btn btn-sm btn-icon btn-light'
                  aria-label='Paneli kapat'
                  onClick={closeAndRestoreFocus}
                >
                  <i className='bi bi-x-lg' aria-hidden='true'></i>
                </button>
              </header>

              <div className='nosi-a11y-panel__body'>
                <fieldset className='nosi-a11y-font-scale'>
                  <legend>Yazı boyutu</legend>
                  <div className='nosi-a11y-segmented'>
                    {FONT_SCALES.map((scale) => (
                      <button
                        type='button'
                        key={scale.value}
                        className={preferences.fontScale === scale.value ? 'active' : ''}
                        aria-pressed={preferences.fontScale === scale.value}
                        onClick={() => setPreference('fontScale', scale.value)}
                      >
                        {scale.label}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <div className='nosi-a11y-options'>
                  <ToggleRow
                    checked={preferences.highContrast}
                    label='Yüksek kontrast'
                    description='Metin ve yüzey ayrımını güçlendirir.'
                    icon='bi-circle-half'
                    onChange={(value) => setPreference('highContrast', value)}
                  />
                  <ToggleRow
                    checked={preferences.grayscale}
                    label='Gri tonlama'
                    description='Renk doygunluğunu arayüz genelinde kaldırır.'
                    icon='bi-droplet-half'
                    onChange={(value) => setPreference('grayscale', value)}
                  />
                  <ToggleRow
                    checked={preferences.underlineLinks}
                    label='Bağlantıları belirginleştir'
                    description='Metin bağlantılarını altı çizili gösterir.'
                    icon='bi-link-45deg'
                    onChange={(value) => setPreference('underlineLinks', value)}
                  />
                  <ToggleRow
                    checked={preferences.increasedLineHeight}
                    label='Rahat satır aralığı'
                    description='Uzun metinlerde okunabilirliği artırır.'
                    icon='bi-text-paragraph'
                    onChange={(value) => setPreference('increasedLineHeight', value)}
                  />
                  <ToggleRow
                    checked={preferences.largeCursor}
                    label='Büyük imleç'
                    description='İşaretçiyi daha kolay takip edilir hale getirir.'
                    icon='bi-cursor-fill'
                    onChange={(value) => setPreference('largeCursor', value)}
                  />
                  <ToggleRow
                    checked={preferences.reduceMotion}
                    label='Hareketi azalt'
                    description='Animasyon ve geçişleri en aza indirir.'
                    icon='bi-pause-circle'
                    onChange={(value) => setPreference('reduceMotion', value)}
                  />
                </div>
              </div>

              <footer className='nosi-a11y-panel__footer'>
                <button type='button' className='btn btn-light w-100' onClick={reset}>
                  <i className='bi bi-arrow-counterclockwise me-2' aria-hidden='true'></i>
                  Varsayılan ayarlara dön
                </button>
              </footer>
            </section>
          </div>,
          document.body
        )}
    </>
  )
}

export {AccessibilityWidget}
